/**
 * React Query hooks for the reply-rate predictor.
 *
 * Used by:
 *   - components/v2/member/EngagementMetricsTab.tsx (single member)
 *   - components/communications/AudienceByLOBPanel.tsx (batch)
 *   - components/communications/insights/* (model freshness banner)
 */

import { useQuery } from '@tanstack/react-query';
import { authFetch } from '@/lib/authFetch';
import type { BatchScoreResponse, ReplyProbability } from './types';

const STALE_MS = 5 * 60 * 1000; // 5 min

function _replyRateBase(drillCboId: number | null | undefined, suffix: string): string {
  return drillCboId && drillCboId > 0
    ? `/api/legacy-trust-ml/cbo/${drillCboId}/reply-rate/${suffix}`
    : `/api/legacy-trust-ml/reply-rate/${suffix}`;
}

async function fetchSingleScore(
  memberId: number,
  drillCboId?: number | null,
): Promise<ReplyProbability> {
  const url = `${_replyRateBase(drillCboId, 'score')}?member_id=${memberId}`;
  const resp = await authFetch(url);
  if (resp.status === 503) {
    throw new Error('model_not_trained');
  }
  if (!resp.ok) {
    throw new Error(`reply-rate score failed: ${resp.status}`);
  }
  return resp.json();
}

async function fetchBatchScores(
  memberIds: number[],
  drillCboId?: number | null,
): Promise<BatchScoreResponse> {
  if (memberIds.length === 0) return { scores: [], skipped_count: 0 };
  const resp = await authFetch(_replyRateBase(drillCboId, 'batch'), {
    method: 'POST',
    jsonBody: { member_ids: memberIds },
  });
  if (resp.status === 503) {
    throw new Error('model_not_trained');
  }
  if (!resp.ok) {
    throw new Error(`reply-rate batch failed: ${resp.status}`);
  }
  return resp.json();
}

/** Score a single member. Returns `null` while loading or on model-not-trained.
 *  `drillCboId` is only honored when caller is the internal CBO; the backend
 *  enforces this. */
export function useReplyProbability(
  memberId: number | null | undefined,
  drillCboId?: number | null,
) {
  return useQuery<ReplyProbability | null>({
    queryKey: ['legacy-trust-ml', 'reply-rate', 'single', memberId, drillCboId ?? null],
    queryFn: () =>
      memberId ? fetchSingleScore(memberId, drillCboId) : Promise.resolve(null),
    enabled: Boolean(memberId && memberId > 0),
    staleTime: STALE_MS,
    retry: (failureCount, err) => {
      if (err instanceof Error && err.message === 'model_not_trained') return false;
      return failureCount < 2;
    },
  });
}

/** Score many members in one POST. */
export function useReplyProbabilityBatch(
  memberIds: number[],
  drillCboId?: number | null,
) {
  // Sort+stringify the ids so the queryKey stays stable across renders.
  const stableKey = [...memberIds].sort((a, b) => a - b).join(',');
  return useQuery<BatchScoreResponse>({
    queryKey: ['legacy-trust-ml', 'reply-rate', 'batch', stableKey, drillCboId ?? null],
    queryFn: () => fetchBatchScores(memberIds, drillCboId),
    enabled: memberIds.length > 0,
    staleTime: STALE_MS,
    retry: (failureCount, err) => {
      if (err instanceof Error && err.message === 'model_not_trained') return false;
      return failureCount < 2;
    },
  });
}
